import {
  ACCENT_PRESETS,
  applyAccentColors,
  isAccentColor,
  isAccentColors,
  type AccentColors,
} from '../shared/accent-colors';
import type { AppearanceMode } from '../shared/desktop-api';
import lightLogoUrl from './assets/paige-logo.png';
import darkLogoUrl from './assets/paige-logo-dark.png';
import { changePersonAppearance, onPersonAppearance, personAppearance } from './person-appearance';

/**
 * Paige's look inside CapturePoint's chrome: one accent colour for the whole product (Dustin, 2026-10-20), Paige
 * blue unless the person picked another in Appearance, and the Paige logo (its dark variant on dark, no backing
 * slab) where CapturePoint showed its wordmark. CapturePoint's own accent mechanism carries it
 * (shared/accent-colors.ts); the desktop applied it from the preload, the browser applies it here
 * (docs/engineering/workbench-port.md, step 4). The pick is the person's, on the server (person-appearance.ts):
 * the dashboard takes the same colour.
 */
/** Paige blue: the dashboard's own accent (apps/web screens/fleet/scene.css), the logo's electric blue. */
export const PAIGE_ACCENT: AccentColors = { light: '#0aa6dc', dark: '#19c3f0' };

// The same two files as packages/ui/src/brand; copied because the package exports no assets.
const logos: Record<AppearanceMode, string> = { light: lightLogoUrl, dark: darkLogoUrl };

type Picked = Partial<AccentColors>;
const MODES = ['light', 'dark'] as const;

/** The colours the person picked; a mode with none follows Paige blue. */
export function readPickedAccents(): Picked {
  const { accent } = personAppearance();
  const picked: Picked = {};
  for (const mode of MODES) {
    const color = accent[mode];
    if (color) picked[mode] = color;
  }
  return picked;
}

/** The colors in use: each mode's pick, or Paige blue for that mode. */
export function readAccentColors(): AccentColors {
  return { ...PAIGE_ACCENT, ...readPickedAccents() };
}

/** A pick is kept even when it equals Paige blue: the person chose it. Rejects with the reason when it was not saved. */
export async function pickAccentColor(mode: AppearanceMode, color: string): Promise<AccentColors> {
  await changePersonAppearance((was) => ({
    ...was,
    accent: { ...was.accent, [mode]: color.toLowerCase() },
  }));
  return readAccentColors();
}

/** Appearance's swatches in Paige: Paige blue first, then CapturePoint's (AccentSettings filters its orange). */
export function paigeAccentPresets(
  mode: AppearanceMode,
): readonly { name: string; color: string }[] {
  return [{ name: 'Paige', color: PAIGE_ACCENT[mode] }, ...ACCENT_PRESETS[mode]];
}

/** Accent tokens and the brand tokens for the mode, on the document root. */
export function applyPaigeTheme(mode: AppearanceMode): void {
  const root = document.documentElement;
  applyAccentColors(root, readAccentColors(), mode);
  root.style.setProperty('--cp-brand-wordmark', `url("${logos[mode]}")`);
  root.style.setProperty('--cp-brand-surface', 'transparent');
}

/** The desktop's accent methods, answered from the person's appearance. */
export function accentBridge(currentTheme: () => AppearanceMode): Record<string, unknown> {
  return {
    getAccentColors: async () => readAccentColors(),
    setAccentColor: async (mode: AppearanceMode, color: string) => {
      const next = await pickAccentColor(mode, color);
      applyPaigeTheme(currentTheme());
      return next;
    },
    onAccentColorsChanged: (callback: (colors: AccentColors) => void) =>
      onPersonAppearance(() => callback(readAccentColors())),
  };
}

/**
 * Applies the theme now and again whenever Paige's ThemeProvider changes `data-theme` or the person's colour
 * changes (on this screen or the dashboard's); returns the stop.
 */
export function followTheme(): () => void {
  const root = document.documentElement;
  const apply = () => {
    const mode = currentMode();
    root.style.colorScheme = mode;
    applyPaigeTheme(mode);
  };
  apply();
  const observer = new MutationObserver(apply);
  observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', apply);
  const stopAppearance = onPersonAppearance(apply);
  return () => {
    observer.disconnect();
    media.removeEventListener('change', apply);
    stopAppearance();
  };
}

function currentMode(): AppearanceMode {
  const explicit = document.documentElement.dataset['theme'];
  if (explicit === 'light' || explicit === 'dark') return explicit;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * What this browser kept before the appearance was the person's (`paige:workbench-accent:v2`, and v1 before
 * 2026-10-06; `paige:workbench-tooltips:v1`): carried up once by the app when the person has picked nothing
 * yet, then forgotten (`forgetLegacyAppearance`). Old defaults a v1 record holds are not picks (CapturePoint
 * 109a46c1, 459aedd8).
 */
const PICKED_KEY = 'paige:workbench-accent:v2';
const OLD_KEY = 'paige:workbench-accent:v1';
const TOOLTIPS_KEY = 'paige:workbench-tooltips:v1';
const OLD_DEFAULTS: AccentColors = { light: '#1764a0', dark: '#d2ecfe' };

type Store = Pick<Storage, 'getItem' | 'removeItem'>;

const picksOf = (value: unknown, skip?: AccentColors): Picked => {
  const picked: Picked = {};
  if (!value || typeof value !== 'object') return picked;
  for (const mode of MODES) {
    const color = (value as Record<string, unknown>)[mode];
    if (isAccentColor(color) && color.toLowerCase() !== skip?.[mode])
      picked[mode] = color.toLowerCase();
  }
  return picked;
};

/** The colours and the tooltips switch this browser kept, or null when it kept none. */
export function legacyBrowserAppearance(
  storage: Store | undefined,
): { accent: Picked; tooltipsOff: boolean } | null {
  try {
    const v2 = storage?.getItem(PICKED_KEY);
    const v1 = storage?.getItem(OLD_KEY);
    const old = v1 == null ? null : (JSON.parse(v1) as unknown);
    const accent =
      v2 != null ? picksOf(JSON.parse(v2)) : isAccentColors(old) ? picksOf(old, OLD_DEFAULTS) : {};
    const tooltipsOff = storage?.getItem(TOOLTIPS_KEY) === 'off';
    return Object.keys(accent).length || tooltipsOff ? { accent, tooltipsOff } : null;
  } catch {
    return null;
  }
}

/** Forgets what this browser kept: the person's appearance is the server's from now on. */
export function forgetLegacyAppearance(storage: Store | undefined): void {
  try {
    for (const key of [PICKED_KEY, OLD_KEY, TOOLTIPS_KEY]) storage?.removeItem(key);
  } catch {
    /* A blocked store keeps nothing anyway. */
  }
}
